import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import DocViewerModal from "../../../../components/modals/docViewerModal/docViewerModal";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import {
  AuthLettersTableColumnsKeys,
  generateAuthLettersTableData,
} from "./authLettersTable";

const AuthLetters = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { medicalPaAuthorization, loading: isLoading } = useSelector(
    (state: RootState) => state.medicalPaAuthorization,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_MODAL) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
  }, [openedModalId]);

  const tableData = useMemo(() => {
    if (!medicalPaAuthorization) return [];
    return generateAuthLettersTableData(medicalPaAuthorization);
  }, [medicalPaAuthorization]);

  const hasLoggedTabView = useRef(false);

  useEffect(() => {
    if (!hasLoggedTabView.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.AUTH_LETTERS_TAB_VIEW,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
        },
      });
      hasLoggedTabView.current = true;
    }

    if (!isLoading && medicalPaAuthorization) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.AUTH_LETTERS_FETCHED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
          auth_letters_count: tableData.length,
        },
      });
    }
  }, [isLoading, medicalPaAuthorization]);

  const handleViewClick = (rowData: any) => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      event_name: EventsName.AUTH_LETTER_VIEW_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
        document_name:
          rowData?.pdfTitle || rowData?.description || "Auth Letter",
      },
    });

    if (rowData?.pdfPath) {
      dispatch(
        setOpenedModalId({
          id: ModalId.DOC_VIEWER_MODAL,
          metaData: {
            filePath: rowData.pdfPath,
            title: rowData.pdfTitle || "Auth Letter",
            hideFooter: true,
          },
        }),
      );
    }
  };

  return (
    <div className="auth-letters--container flex flex-col gap-2">
      {isLoading ? (
        <LoaderMessage message="Fetching Auth Letters..." />
      ) : (
        <>
          {tableData.length > 0 ? (
            <div className="auth-letters--table">
              <CustomTable
                tableHeaders={AuthLettersTableColumnsKeys}
                tableData={tableData}
                itemsPerPage={tableData.length}
                pagesPerView={5}
                count={tableData.length}
                onReviewButtonClick={handleViewClick}
                tableName="authLetters"
                hideSearchBar={true}
                hidePagination={true}
              />
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-lg bg-gray-50">
              <p className="text-center text-gray-500">No auth letters found</p>
            </div>
          )}
        </>
      )}
      {openedModalId === ModalId.DOC_VIEWER_MODAL && (
        <DocViewerModal
          onClose={() => {
            dispatch(closeModal());
          }}
          type="clinical_attachment"
        />
      )}
    </div>
  );
};

export default AuthLetters;
